/**
 * Empty state shown in the chart area when no metrics are selected.
 * Presents a small set of pre-built example queries the user can apply
 * with one click. Each example is a static query shape that gets pushed
 * into the form via applyNlqQuery — no AI involved.
 */
import React, { useCallback, useContext, useMemo } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import styled, { keyframes } from 'styled-components';
import { Box, Flex, Heading, Text } from 'core/components';
import { TbActivity, TbChartLine, TbCloud, TbNetwork, TbRouter, TbWorldWww } from 'react-icons/tb';
import type { IconType } from 'react-icons';
import type { Measurement, Query } from '@kentik/ui-shared/query/query.types';
import type { UdeFormProps } from '../form.types';
import { applyNlqQuery } from './applyNlqQuery';

type UdeForm = UdeFormProps['form'];

interface UdeEmptyStateProps {
  form: UdeForm;
  measurements: Measurement[];
  onQueryApplied?: (query: Query) => void;
  onRunQuery?: () => void;
}

interface AvailabilityContext {
  hasClouds: boolean;
  hasSynthetics: boolean;
  hasMeasurement: (path: string) => boolean;
}

interface ExampleDef {
  key: string;
  title: string;
  description: string;
  icon: IconType;
  /** Optional gate — example is hidden when this returns false. */
  isAvailable?: (ctx: AvailabilityContext) => boolean;
  /** Build the Query that will be applied to the form. */
  buildQuery: () => Query;
}

const DEFAULT_LOOKBACK_SECONDS = 3600;

/** Helper: build a Query with sensible defaults so applyNlqQuery doesn't trip on missing fields. */
function makeQuery(partial: Partial<Query> & Pick<Query, 'measurement'>): Query {
  return {
    metrics: [],
    dimensions: [],
    rollups: [],
    sort: [],
    time: { lookback: DEFAULT_LOOKBACK_SECONDS } as Query['time'],
    ...partial
  } as Query;
}

const EXAMPLES: ExampleDef[] = [
  {
    key: 'traffic-by-site',
    title: 'Top traffic by site',
    description: 'Inbound + outbound bits/sec grouped by site.',
    icon: TbWorldWww,
    isAvailable: ({ hasMeasurement }) => hasMeasurement('/traffic'),
    buildQuery: () =>
      makeQuery({
        measurement: '/traffic',
        metrics: [{ name: 'both_bits_per_sec' }],
        dimensions: [{ name: 'i_device_site_name' }],
        viz: { type: 'stackedArea', limit: 10 }
      })
  },
  {
    key: 'nms-oper-status',
    title: 'Interface Oper Status',
    description: 'Operational status of each interface, by device.',
    icon: TbNetwork,
    isAvailable: ({ hasMeasurement }) => hasMeasurement('/metrics/interfaces/counters'),
    buildQuery: () =>
      makeQuery({
        measurement: '/metrics/interfaces/counters',
        metrics: [{ name: 'oper-status' }],
        dimensions: [{ name: 'device_name' }, { name: 'name' }],
        viz: { type: 'line', limit: 10 }
      })
  },
  {
    key: 'nms-interface-utilization',
    title: 'Interface Utilization',
    description: 'Inbound utilization % per interface, top talkers.',
    icon: TbChartLine,
    isAvailable: ({ hasMeasurement }) => hasMeasurement('/metrics/interfaces/counters'),
    buildQuery: () =>
      makeQuery({
        measurement: '/metrics/interfaces/counters',
        metrics: [{ name: 'in-utilization' }, { name: 'out-utilization' }],
        dimensions: [{ name: 'device_name' }, { name: 'name' }],
        viz: { type: 'line', limit: 10 }
      })
  },
  {
    key: 'bgp-session-states',
    title: 'BGP session states',
    description: 'Session state for every BGP neighbor across devices.',
    icon: TbRouter,
    isAvailable: ({ hasMeasurement }) => hasMeasurement('/metrics/protocols/bgp/neighbors'),
    buildQuery: () =>
      makeQuery({
        measurement: '/metrics/protocols/bgp/neighbors',
        metrics: [{ name: 'session-state' }],
        dimensions: [{ name: 'device_name' }, { name: 'transport/remote-address' }],
        viz: { type: 'line', limit: 10 }
      })
  },
  {
    key: 'cloud-traffic',
    title: 'Cloud traffic by provider',
    description: 'Bits/sec broken out by cloud flow log provider.',
    icon: TbCloud,
    isAvailable: ({ hasClouds, hasMeasurement }) => hasClouds && hasMeasurement('/traffic'),
    buildQuery: () =>
      makeQuery({
        measurement: '/traffic',
        metrics: [{ name: 'both_bits_per_sec' }],
        dimensions: [{ name: 'kt_cloud_provider' }],
        viz: { type: 'stackedArea', limit: 10 }
      })
  },
  {
    key: 'synth-ping-latency',
    title: 'Ping latency by agent',
    description: 'Average ping latency between synthetic agents and targets.',
    icon: TbActivity,
    isAvailable: ({ hasSynthetics, hasMeasurement }) => hasSynthetics && hasMeasurement('/synthetics/ping'),
    buildQuery: () =>
      makeQuery({
        measurement: '/synthetics/ping',
        metrics: [{ name: 'ping_latency_avg' }],
        dimensions: [{ name: 'src_agent' }, { name: 'dst_task_target' }],
        viz: { type: 'line', limit: 10 }
      })
  },
  {
    key: 'synth-http-latency',
    title: 'HTTP latency by target',
    description: 'Average HTTP response latency by synthetic test target.',
    icon: TbActivity,
    isAvailable: ({ hasSynthetics, hasMeasurement }) => hasSynthetics && hasMeasurement('/synthetics/http'),
    buildQuery: () =>
      makeQuery({
        measurement: '/synthetics/http',
        metrics: [{ name: 'http_latency_avg' }],
        dimensions: [{ name: 'src_agent' }, { name: 'dst_task_target' }],
        viz: { type: 'line', limit: 10 }
      })
  }
];

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const Container = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 48px 24px;
  text-align: center;
  background: radial-gradient(ellipse at 50% 40%, rgba(45, 114, 210, 0.04) 0%, transparent 70%);
  overflow: auto;
`;

const AnimatedHeading = styled(Heading)`
  margin-bottom: 8px;
  background: linear-gradient(135deg, #1a3a5c, #2d72d2, #634dbf);
  background-size: 200% auto;
  animation:
    ${fadeInUp} 0.5s ease-out both,
    ${shimmer} 6s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(Text)`
  margin-bottom: 32px;
  animation: ${fadeInUp} 0.5s ease-out 0.1s both;
`;

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 820px;
  animation: ${fadeInUp} 0.5s ease-out 0.2s both;
`;

const ExampleCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColors.thin || 'rgba(128, 128, 128, 0.25)'};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: inherit;
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    border-color 0.12s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme }) => theme.colors.primary || '#2d72d2'};
    box-shadow: 0 4px 16px rgba(45, 114, 210, 0.12);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary || '#2d72d2'};
    outline-offset: 2px;
  }
`;

const CardIcon = styled(Flex)`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  background: rgba(45, 114, 210, 0.1);
  color: ${({ theme }) => theme.colors.primary || '#2d72d2'};
  font-size: 18px;
  flex: none;
`;

const UdeEmptyState = observer(({ form, measurements, onQueryApplied, onRunQuery }: UdeEmptyStateProps) => {
  const { $clouds, $syn } = useContext(MobXProviderContext);

  const measurementPaths = useMemo(() => new Set(measurements.map((m) => m.measurement)), [measurements]);

  const availability: AvailabilityContext = useMemo(
    () => ({
      hasClouds: !!$clouds?.hasClouds,
      hasSynthetics: !!$syn?.tests?.unfilteredSize,
      hasMeasurement: (path: string) => measurementPaths.has(path)
    }),
    [$clouds?.hasClouds, $syn?.tests?.unfilteredSize, measurementPaths]
  );

  const visibleExamples = useMemo(
    () => EXAMPLES.filter((ex) => (ex.isAvailable ? ex.isAvailable(availability) : true)),
    [availability]
  );

  const handleExampleClick = useCallback(
    (example: ExampleDef) => {
      const query = example.buildQuery();
      applyNlqQuery(form, query, measurements);
      onQueryApplied?.(query);
      onRunQuery?.();
    },
    [form, measurements, onQueryApplied, onRunQuery]
  );

  return (
    <Container>
      <AnimatedHeading level={2} fontWeight="bold">
        Build a chart
      </AnimatedHeading>
      <Subtitle color="muted" fontSize={16}>
        Pick a starting point below, or use the left sidebar to build a query.
      </Subtitle>

      <ExampleGrid>
        {visibleExamples.map((example) => {
          const Icon = example.icon;
          return (
            <ExampleCard
              key={example.key}
              type="button"
              onClick={() => handleExampleClick(example)}
              aria-label={`Apply example: ${example.title}`}
            >
              <Flex alignItems="center" gap="10px" width="100%">
                <CardIcon>
                  <Icon />
                </CardIcon>
                <Box flex={1} minWidth={0}>
                  <Text fontWeight="bold" fontSize={14}>
                    {example.title}
                  </Text>
                </Box>
              </Flex>
              <Text color="muted" small>
                {example.description}
              </Text>
            </ExampleCard>
          );
        })}
      </ExampleGrid>
    </Container>
  );
});

export default UdeEmptyState;
